import { format } from "date-fns";
import type {
  SubscriptionPlanFeature,
  SubscriptionPlan,
} from "@/types/subscription-plan";
import type {
  TenantMergedFeature,
  TenantSubscription,
  TenantSubscriptionPlanFeature,
} from "@/types/tenant-subscription";

export interface NormalizedPlanFeature {
  key: string;
  title?: string;
  type?: string;
  value?: string;
}

export function formatMoney(price: number | string, currency: string): string {
  const raw =
    typeof price === "number" ? price : Number.parseFloat(String(price));
  const amount = Number.isFinite(raw) ? raw : 0;
  const code = currency.trim() === "" ? "USD" : currency.trim().toUpperCase();

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
    }).format(amount);
  } catch {
    return `${amount} ${code}`;
  }
}

export function formatDateTime(value: string | null): string {
  if (value === null) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return format(date, "dd MMM yyyy HH:mm");
}

export function getStatusVariant(status: string) {
  switch (status.toLowerCase()) {
    case "active":
      return "service_black";
    case "incomplete":
    case "trialing":
      return "new_outline";
    case "canceled":
    case "unpaid":
      return "new_destructive";
    default:
      return "new_secondary";
  }
}

export function formatMergedFeatureValue(feature: TenantMergedFeature): string {
  if (typeof feature.value === "boolean") {
    return feature.value ? "true" : "false";
  }

  return String(feature.value);
}

export function getMergedFeatureValueVariant(feature: TenantMergedFeature) {
  if (typeof feature.value === "boolean") {
    return feature.value ? "new_default" : "new_secondary";
  }

  if (typeof feature.value === "number") {
    return "number_default";
  }

  return "new_secondary";
}

export function normalizeSubscriptionPlanFeature(
  feature: SubscriptionPlanFeature,
): NormalizedPlanFeature {
  const key =
    feature.key?.trim() || feature.feature?.key?.trim() || feature.featureId;

  return {
    key,
    title: feature.title ?? feature.feature?.title,
    type: feature.type ?? feature.feature?.type,
    value: feature.value,
  };
}

export function normalizeTenantPlanFeature(
  feature: TenantSubscriptionPlanFeature,
): NormalizedPlanFeature {
  return {
    key: feature.key,
    title: feature.title,
    type: feature.type,
    value: feature.value,
  };
}

export function currentPlanIds(
  subscriptions: TenantSubscription[],
): Set<string> {
  return new Set(
    subscriptions
      .filter((subscription) =>
        ["active", "trialing", "incomplete", "past_due"].includes(
          subscription.status.toLowerCase(),
        ),
      )
      .map((subscription) => subscription.plan.id),
  );
}

export function isIncompleteExpiredSubscriptionStatus(status: string): boolean {
  return status.toLowerCase() === "incomplete_expired";
}

export function findPlanById(
  plans: SubscriptionPlan[],
  planId: string,
): SubscriptionPlan | null {
  return plans.find((plan) => plan.id === planId) ?? null;
}
